import { useEffect, useState, type RefObject, useCallback, useRef } from 'react'
import WaveSurfer from 'wavesurfer.js/dist/wavesurfer.js'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore.ts'

type IUseWaveformProps = {
  id: string
  url: string
  ref: RefObject<HTMLDivElement | null>
  autoplay?: boolean
  height?: number
  setPlaying?: (value: boolean) => void
  setDuration?: (value: number) => void
  onErrorLoad?: (e: unknown) => void
}

export type IOnWaveformPlayProps = {
  playing: boolean
  onStart?: () => void
  onFinish?: () => void
  onError?: (e: unknown) => void
}

export const useWaveform = ({
  id,
  url,
  ref,
  autoplay,
  height = 24,
  setPlaying,
  setDuration,
  onErrorLoad,
}: IUseWaveformProps) => {
  const [waveform, setWaveform] = useState<WaveSurfer | null>(null)
  const firstLoadingRef = useRef(false)

  useEffect(() => {
    let waveform_cache: WaveSurfer | null = null

    try {
      if (!ref.current) return

      const audio = new Audio(url)
      audio.preload = 'none'
      const currentPeaks = Array(1000).fill(0.1)

      const w = WaveSurfer.create({
        barWidth: 0,
        barRadius: 0,
        barGap: 0,
        cursorWidth: 0,
        cursorColor: '#068FF9',
        container: ref.current,
        waveColor: '#ACB3B9',
        progressColor: '#068FF9',
        height: height,
        autoplay: autoplay,

        media: audio,
        peaks: [currentPeaks],
      })

      w.on('play', () => {
        uiStore.setPlayAudioId(id)
        setPlaying?.(true)
      })
      w.on('pause', () => setPlaying?.(false))
      w.on('interaction', (time) => {
        setDuration?.(time)
      })
      w.on('audioprocess', (time) => {
        if (time === w.getDuration()) {
          setPlaying?.(false)
          setDuration?.(w.getDuration())
        } else {
          setDuration?.(time)
        }
      })

      setWaveform(w)
      waveform_cache = w
    } catch (e) {
      logger.error(e)
    }

    return () => {
      waveform_cache?.stop()
      waveform_cache?.destroy()
      waveform_cache = null
      setWaveform(null)
    }
  }, [])

  const onWaveformPlay = useCallback(
    async ({ playing, onFinish, onStart, onError }: IOnWaveformPlayProps) => {
      if (!waveform) return

      if (playing) {
        waveform.pause()
      } else {
        const media = waveform.getMediaElement()

        if (media.error) media.load()
        if (!firstLoadingRef.current) onStart?.()

        firstLoadingRef.current = true

        await waveform
          .play()
          .then(() => {
            onFinish?.()
          })
          .catch((e) => {
            onError?.(e)
            onErrorLoad?.(e)

            firstLoadingRef.current = false
          })
      }
    },
    [waveform]
  )

  return {
    waveform: waveform,
    setWaveform: setWaveform,
    onWaveformPlay: onWaveformPlay,
  }
}
